import {
  getVideoModelDisabledReasons,
  getVideoReferenceRequirements,
} from "~/common/video/videoModels"
import {
  ArrowRightOutlined,
  ScanOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons"
import { Button, Flex, Tooltip, Typography } from "antd"
import { Alert } from "~/components/Alert"
import classNames from "classnames"
import { useNavigate } from "react-router"
import {
  VIDEO_TEXT_FIDELITY_WARNING,
  VIDEO_TOOL_LABELS,
} from "~/common/constants"
import { ROUTER } from "~/common/router"
import {
  AnalyticsAction,
  AnalyticsContext,
  AssetPromptInput,
  ImageResourceIcon,
  PointCostText,
  ReferencePreviewList,
  ResourceLibraryModal,
  SendIcon,
  ScriptCreationIcon,
  VideoParameterSelect,
  VideoGenerateConfirm,
  ICPBlock,
  Text,
} from "~/components"
import { useThemeStore } from "~/store/theme"
import { InspirationGallery } from "~/components/InspirationGallery"
import { RecreateToolSection } from "./RecreateToolSection"
import { useHomeComposer } from "./useHomeComposer"
import styles from "./index.css"

const { Title } = Typography

const CREATION_TOOL_ENTRIES = [
  {
    description: "自主编写视频脚本，完全自定义镜头场景，自由生成全新视频",
    icon: ScriptCreationIcon,
    title: VIDEO_TOOL_LABELS.origin,
    type: "create",
  },
  {
    description:
      "深度解析爆款视频脚本逻辑，提炼钩子、卖点、转化话术，输出可直接使用的脚本模板",
    icon: ScanOutlined,
    path: ROUTER.ANALYSIS,
    title: "视频脚本拆解",
    type: "analysis",
  },
  {
    description:
      "一键修复模糊、压缩、低清素材，智能提升视频分辨率，轻松产出高清电商短视频",
    icon: ThunderboltOutlined,
    path: ROUTER.VIDEO_ENHANCEMENT,
    title: "视频画质提升",
    type: "enhancement",
  },
] as const

export const Home = () => {
  const navigate = useNavigate()
  const isDarkMode = useThemeStore((state) => state.mode === "dark")
  const {
    allReferences,
    canGenerate,
    composerRef,
    expandPrompt,
    generateVideo,
    handleAssetConfirm,
    isAssetModalOpen,
    isDragging,
    isPromptExpanded,
    isSubmittingVideoTask,
    materialType,
    maxAudioCount,
    maxImageCount,
    maxReferenceCount,
    openAssetModal,
    prompt,
    promptInputRef,
    referenceSummary,
    referenceUrlPayload,
    references,
    remoteFetchingReferenceCount,
    removeReference,
    reorderReferences,
    setIsAssetModalOpen,
    setMentionedResources,
    setPrompt,
    setVideoOptions,
    shouldShowTextFidelityWarning,
    surfaceProps,
    togglePrompt,
    uploadingReferenceCount,
    videoModel,
    videoOptions,
    videoPointCost,
  } = useHomeComposer()

  return (
    <AnalyticsContext category="首页">
      <main className={classNames(styles.page, isDarkMode && styles.darkPage)}>
        <Flex
          className={styles.hero}
          align="center"
          vertical
        >
          <Title
            className={styles.heroTitle}
            level={1}
          >
            咔嗒一下，<span>爆款即成</span>
          </Title>
          <Text
            size="m"
            className={styles.heroSubtitle}
          >
            智能分析爆款结构，一键复刻高转化视频，让每个商品都成为爆款
          </Text>

          <RecreateToolSection />

          <section className={styles.toolsSection}>
            <Text
              size={18}
              className={styles.sectionTitle}
              strong
            >
              AI 创作工具
            </Text>
            <Flex
              className={styles.toolGrid}
              gap={18}
              wrap
            >
              {CREATION_TOOL_ENTRIES.map((entry) => {
                const Icon = entry.icon
                const isCreateEntry = entry.type === "create"

                return (
                  <AnalyticsAction
                    action={`点击${isCreateEntry && isPromptExpanded ? `收起${VIDEO_TOOL_LABELS.origin}` : entry.title}`}
                    key={entry.type}
                    prefix="首页AI创作工具"
                  >
                    <button
                      className={classNames(
                        styles.toolCard,
                        styles[entry.type],
                      )}
                      type="button"
                      aria-expanded={
                        isCreateEntry ? isPromptExpanded : undefined
                      }
                      onClick={
                        isCreateEntry
                          ? togglePrompt
                          : () => navigate(entry.path)
                      }
                    >
                      <Flex
                        className={styles.toolCardAction}
                        gap={4}
                        vertical
                        align="flex-start"
                      >
                        <Flex
                          style={{ width: "100%" }}
                          gap={14}
                          align="center"
                        >
                          <Flex
                            className={styles.toolCardIcon}
                            align="center"
                            justify="center"
                          >
                            <Icon />
                          </Flex>
                          <Text
                            strong
                            size="l"
                          >
                            {entry.title}
                          </Text>
                          {isCreateEntry && isPromptExpanded ? (
                            <Text className={styles.toolCardArrow}>收起</Text>
                          ) : (
                            <ArrowRightOutlined
                              className={styles.toolCardArrow}
                            />
                          )}
                        </Flex>
                        <Text type="secondary">{entry.description}</Text>
                      </Flex>
                    </button>
                  </AnalyticsAction>
                )
              })}
            </Flex>

            {isPromptExpanded ? (
              <section
                ref={composerRef}
                className={styles.composer}
                {...surfaceProps}
                onClick={expandPrompt}
              >
                <Flex
                  className={classNames(
                    styles.promptArea,
                    styles.promptAreaExpanded,
                    isDragging && styles.promptAreaDragging,
                  )}
                  data-home-prompt-input
                  tabIndex={-1}
                  gap={12}
                  vertical
                  onClick={expandPrompt}
                >
                  {isDragging ||
                  uploadingReferenceCount > 0 ||
                  remoteFetchingReferenceCount > 0 ? (
                    <Flex
                      className={styles.promptDropOverlay}
                      align="center"
                      justify="center"
                    >
                      <Text strong>
                        {remoteFetchingReferenceCount > 0
                          ? "获取远程素材中"
                          : uploadingReferenceCount > 0
                            ? `上传中...（${uploadingReferenceCount}）`
                            : "上传参考素材"}
                      </Text>
                    </Flex>
                  ) : null}
                  <AssetPromptInput
                    ref={promptInputRef}
                    className={styles.promptInput}
                    mentionableResources={references}
                    placeholder={`上传图片 / 音频参考（${getVideoReferenceRequirements(videoModel)}）输入文字或@参考内容`}
                    onMentionResourcesChange={setMentionedResources}
                    onTextChange={setPrompt}
                    value={prompt}
                  />
                  <ReferencePreviewList
                    references={references}
                    sortable={
                      referenceUrlPayload.imageUrls.length > 1 &&
                      uploadingReferenceCount === 0 &&
                      remoteFetchingReferenceCount === 0 &&
                      !isSubmittingVideoTask
                    }
                    onRemove={removeReference}
                    onReorder={reorderReferences}
                  />
                </Flex>
                <Flex
                  className={styles.toolbar}
                  align="center"
                  gap={10}
                  wrap
                >
                  <Tooltip title="添加参考素材">
                    <Button
                      className={styles.iconTool}
                      icon={
                        <ImageResourceIcon
                          className={styles.imageResourceIcon}
                        />
                      }
                      shape="circle"
                      onClick={openAssetModal}
                    />
                  </Tooltip>
                  <VideoParameterSelect
                    modelDisabledReasons={getVideoModelDisabledReasons({
                      resources: allReferences,
                    })}
                    value={videoOptions}
                    onChange={setVideoOptions}
                  />
                  <Tooltip title="引用资源">
                    <Button
                      className={styles.iconTool}
                      shape="circle"
                      onClick={(event) =>
                        promptInputRef.current?.openMentionMenu(
                          event.currentTarget,
                        )
                      }
                    >
                      @
                    </Button>
                  </Tooltip>
                  <Text
                    size="s"
                    className={styles.referenceCount}
                  >
                    {allReferences.length}/{maxReferenceCount}
                    {referenceSummary ? ` · ${referenceSummary}` : ""}
                  </Text>
                  <PointCostText cost={videoPointCost} />
                  <AnalyticsAction
                    action="点击发送"
                    fields={{ materialType }}
                    prefix="生视频输入框"
                  >
                    <VideoGenerateConfirm
                      model={videoModel}
                      pointCost={videoPointCost}
                      onConfirm={generateVideo}
                      disabled={!canGenerate || isSubmittingVideoTask}
                      okButtonProps={{
                        disabled: !canGenerate,
                        loading: isSubmittingVideoTask,
                      }}
                    >
                      <Button
                        aria-label="生成视频"
                        disabled={!canGenerate}
                        icon={<SendIcon className={styles.sendIcon} />}
                        loading={isSubmittingVideoTask}
                        shape="circle"
                        type="primary"
                        styles={{ icon: { height: 18 } }}
                      />
                    </VideoGenerateConfirm>
                  </AnalyticsAction>
                </Flex>
                {shouldShowTextFidelityWarning ? (
                  <Alert
                    showIcon
                    title={VIDEO_TEXT_FIDELITY_WARNING}
                    level="weak"
                  />
                ) : null}
              </section>
            ) : null}
          </section>
        </Flex>

        <InspirationGallery title={VIDEO_TOOL_LABELS.oneClickCopy} />
        <ICPBlock />
      </main>

      <ResourceLibraryModal
        open={isAssetModalOpen}
        confirmText="添加参考"
        maxCountByKind={{ image: maxImageCount, audio: maxAudioCount }}
        resourceType={["image", "audio"]}
        selectedResources={references}
        title="添加参考素材"
        onCancel={() => setIsAssetModalOpen(false)}
        onConfirm={handleAssetConfirm}
      />
    </AnalyticsContext>
  )
}
